import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FaAngleLeft,
  FaPlay,
  FaPause,
  FaStop,
  FaTree,
  FaCloudShowersHeavy,
  FaFire,
  FaStar,
  FaMusic,
  FaWater,
  FaCloudBolt,
  FaWind,
  FaCrown,
  FaHandsPraying,
  FaMoon,
  FaSun,
  FaVolumeXmark,
  FaVolumeLow,
} from "react-icons/fa6";
import { useTranslation } from "react-i18next";

// Імпорти
import { getTopMenuData } from "../../api/mainApi";
import Premium from "../premium/Premium";

// --- ТЕМИ (Фон + Аудіо залишаємо захардкодженими через іконки) ---
const THEMES = [
  {
    id: "none",
    labelKey: "themes.none",
    defaultLabel: "Тиша",
    icon: FaVolumeXmark,
    image: "/themes/quiet.jpg",
    overlay: "from-[#061014]/85 to-[#061014]/85",
    audioUrl: null,
    isPremium: false,
  },
  {
    id: "space",
    labelKey: "themes.space",
    defaultLabel: "Космос",
    icon: FaStar,
    image: "/themes/space.jpg",
    overlay: "from-[#061014]/95 via-slate-950/85 to-[#061014]/95",
    audioUrl: "/sounds/windchime-drone.mp3",
    isPremium: true,
  },
  {
    id: "rain",
    labelKey: "themes.rain",
    defaultLabel: "Дощ",
    icon: FaCloudShowersHeavy,
    image: "/themes/rain.jfif",
    overlay: "from-black/95 via-slate-950/85 to-black/95",
    audioUrl: "/sounds/rain-water-dripping-softly.mp3",
    isPremium: false,
  },
  {
    id: "forest",
    labelKey: "themes.forest",
    defaultLabel: "Ліс",
    icon: FaTree,
    image: "/themes/forest.jfif",
    overlay: "from-black/95 via-stone-950/85 to-black/95",
    audioUrl: "/sounds/forest.mp3",
    isPremium: false,
  },
  {
    id: "fire",
    labelKey: "themes.fire",
    defaultLabel: "Багаття",
    icon: FaFire,
    image: "/themes/campfire.jpg",
    overlay: "from-black/95 via-stone-950/85 to-black/95",
    audioUrl: "/sounds/daytime-forest-bonfire.mp3",
    isPremium: true,
  },
  {
    id: "ocean",
    labelKey: "themes.ocean",
    defaultLabel: "Океан",
    icon: FaWater,
    image: "/themes/ocean.jpg",
    overlay: "from-black/95 via-slate-950/85 to-black/95",
    audioUrl: "/sounds/waves-crashing-on-rock-beach.mp3",
    isPremium: true,
  },
  {
    id: "storm",
    labelKey: "themes.storm",
    defaultLabel: "Шторм",
    icon: FaCloudBolt,
    image: "/themes/storm.jpg",
    overlay: "from-black/95 via-slate-950/85 to-black/95",
    audioUrl: "/sounds/thunderstorm.mp3",
    isPremium: true,
  },
  {
    id: "wind",
    labelKey: "themes.wind",
    defaultLabel: "Вітер",
    icon: FaWind,
    image: "/themes/wind.jfif",
    overlay: "from-black/95 via-slate-950/85 to-black/95",
    audioUrl: "/sounds/wind.mp3",
    isPremium: false,
  },
  {
    id: "night",
    labelKey: "themes.night",
    defaultLabel: "Ніч",
    icon: FaMoon,
    image: "/themes/night.jfif",
    overlay: "from-black/85 via-slate-950/85 to-black/85",
    audioUrl: "/sounds/night.mp3",
    isPremium: false,
  },
  {
    id: "hope",
    labelKey: "themes.hope",
    defaultLabel: "Надія",
    icon: FaHandsPraying,
    image: "/themes/hope.jpg",
    overlay: "from-black/95 via-slate-950/85 to-black/95",
    audioUrl: "/sounds/hope.mp3",
    isPremium: true,
  },
  {
    id: "sunrise",
    labelKey: "themes.sunrise",
    defaultLabel: "Світанок",
    icon: FaSun,
    image: "/themes/sunrise.jpg",
    overlay: "from-black/95 via-slate-950/85 to-black/95",
    audioUrl: "/sounds/sunrise.mp3",
    isPremium: false,
  },
  {
    id: "forest-2",
    labelKey: "themes.forest-2",
    defaultLabel: "Ліс 2",
    icon: FaTree,
    image: "/themes/forest-2.jfif",
    overlay: "from-black/85 via-slate-950/85 to-black/85",
    audioUrl: "/sounds/forest-2.mp3",
    isPremium: true,
  },
];

function MeditationPlayer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Отримуємо дані з React Router State
  const data = location.state?.practiceData;
  const isGuided = data?.type === "guided";

  // Референси
  const ambientRef = useRef(null);
  const voiceRef = useRef(null);

  // Візуалізатор
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const animationRef = useRef(null);
  const smoothedIntensityRef = useRef(0);

  // Стан
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  // Безпечна ініціалізація станів таймерів
  const [sessionTimeLeft, setSessionTimeLeft] = useState(
    data ? (data.duration || data.totalDurationMinutes || 0) * 60 : 0,
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [stepTimeLeft, setStepTimeLeft] = useState(
    data?.type === "breathing" ? data.cycle?.[0]?.duration || 0 : 0,
  );

  const [activeTheme, setActiveTheme] = useState(THEMES[2]);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);

  const [ambientVolume, setAmbientVolume] = useState(0.2);
  const [vocalIntensity, setVocalIntensity] = useState(0);

  // Преміум
  const [isUserPremium, setIsUserPremium] = useState(false);
  const [showPremium, setShowPremium] = useState(false);

  // Якщо даних немає, перенаправляємо назад
  useEffect(() => {
    if (!data) {
      navigate("/");
    }
  }, [data, navigate]);

  // Універсальна функція плавного згасання / наростання
  const fadeAudio = (audio, targetVolume, duration, callback) => {
    if (!audio) return;
    if (audio._fadeInterval) clearInterval(audio._fadeInterval);

    const startVolume = audio.volume;
    const distance = targetVolume - startVolume;
    const steps = 20;
    const stepTime = duration / steps;
    let currentStep = 0;

    if (distance === 0 || duration === 0) {
      audio.volume = targetVolume;
      if (callback) callback();
      return;
    }

    audio._fadeInterval = setInterval(() => {
      currentStep++;
      let newVol = startVolume + distance * (currentStep / steps);
      if (newVol > 1) newVol = 1;
      if (newVol < 0) newVol = 0;
      audio.volume = newVol;

      if (currentStep >= steps) {
        clearInterval(audio._fadeInterval);
        if (callback) callback();
      }
    }, stepTime);
  };

  // --- 0. Ініціалізація теми, гучності та преміуму ---
  useEffect(() => {
    const savedVol = localStorage.getItem("meditation_ambient_volume");
    if (savedVol !== null) setAmbientVolume(parseFloat(savedVol));

    const savedThemeId = localStorage.getItem("meditation_theme_id");
    if (savedThemeId) {
      const foundTheme = THEMES.find((t) => t.id === savedThemeId);
      if (foundTheme) setActiveTheme(foundTheme);
    }

    // Запит без tg.initData! Interceptor зробить все сам.
    async function fetchPremiumStatus() {
      try {
        const apiData = await getTopMenuData();
        setIsUserPremium(apiData.premium > 0);
      } catch (error) {
        console.error("Помилка отримання статусу преміум:", error);
      }
    }
    fetchPremiumStatus();
  }, []);

  const handleAudioLoad = (e) => {
    if (isGuided && e.target.duration) {
      setSessionTimeLeft(Math.floor(e.target.duration));
    }
  };

  const handleWaiting = () => setIsBuffering(true);
  const handlePlaying = () => setIsBuffering(false);

  // --- 1. Візуалізатор Web Audio API ---
  useEffect(() => {
    if (!isGuided || !voiceRef.current) return;

    if (!audioContextRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 128;

      try {
        sourceNodeRef.current =
          audioContextRef.current.createMediaElementSource(voiceRef.current);
        sourceNodeRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
      } catch (error) {
        console.warn("AudioContext already connected");
      }
    }

    if (isPlaying && audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume();
    }

    const updateVisuals = () => {
      if (analyserRef.current && isPlaying) {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        let sum = 0;
        for (let i = 2; i < 15; i++) sum += dataArray[i];
        const avg = sum / 13;

        let rawIntensity = (avg / 255) * 1.5;
        if (rawIntensity > 1) rawIntensity = 1;

        smoothedIntensityRef.current =
          smoothedIntensityRef.current * 0.85 + rawIntensity * 0.15;
        setVocalIntensity(smoothedIntensityRef.current);
      } else {
        smoothedIntensityRef.current = smoothedIntensityRef.current * 0.9;
        setVocalIntensity(smoothedIntensityRef.current);
      }
      animationRef.current = requestAnimationFrame(updateVisuals);
    };

    updateVisuals();
    return () => cancelAnimationFrame(animationRef.current);
  }, [isGuided, isPlaying]);

  // --- 2. Керування аудіо ---
  useEffect(() => {
    const ambient = ambientRef.current;
    const voice = voiceRef.current;

    if (isPlaying) {
      if (activeTheme.id === "none") {
        if (ambient) fadeAudio(ambient, 0, 500, () => ambient.pause());
      } else if (ambient && activeTheme.audioUrl) {
        if (!ambient.src.includes(activeTheme.audioUrl)) {
          ambient.src = activeTheme.audioUrl;
        }
        ambient
          .play()
          .then(() => {
            fadeAudio(ambient, ambientVolume, 1000);
          })
          .catch(() => {});
      }
      if (voice && data?.voiceUrl) voice.play().catch(() => {});
    } else {
      if (ambient) fadeAudio(ambient, 0, 800, () => ambient.pause());
      if (voice) voice.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, activeTheme, data?.voiceUrl]);

  const handleVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    setAmbientVolume(vol);
    localStorage.setItem("meditation_ambient_volume", vol);
    if (ambientRef.current) {
      if (ambientRef.current._fadeInterval)
        clearInterval(ambientRef.current._fadeInterval);
      ambientRef.current.volume = vol;
    }
  };

  // --- 3. Плавне затухання наприкінці сесії ---
  useEffect(() => {
    if (
      isPlaying &&
      sessionTimeLeft === 4 &&
      activeTheme.id !== "none" &&
      ambientRef.current
    ) {
      fadeAudio(ambientRef.current, 0, 4000);
    }
  }, [sessionTimeLeft, isPlaying, activeTheme.id]);

  useEffect(() => {
    let timer;
    if (isPlaying && sessionTimeLeft > 0) {
      timer = setInterval(() => {
        setSessionTimeLeft((prev) => prev - 1);
        if (data?.type === "breathing") {
          setStepTimeLeft((prev) => {
            if (prev <= 1) {
              const nextIndex = (stepIndex + 1) % (data?.cycle?.length || 1);
              setStepIndex(nextIndex);
              return data?.cycle?.[nextIndex]?.duration || 0;
            }
            return prev - 1;
          });
        }
      }, 1000);
    } else if (sessionTimeLeft <= 0 && isPlaying) {
      setIsPlaying(false);
      setIsFinished(true);
    }
    return () => clearInterval(timer);
  }, [isPlaying, sessionTimeLeft, stepIndex, data]);

  const togglePlay = () => setIsPlaying(!isPlaying);

  const stopSession = () => {
    setIsPlaying(false);
    if (data?.type === "breathing") {
      setSessionTimeLeft(
        (data?.duration || data?.totalDurationMinutes || 0) * 60,
      );
      setStepIndex(0);
      setStepTimeLeft(data?.cycle?.[0]?.duration || 0);
    } else if (voiceRef.current && voiceRef.current.duration) {
      setSessionTimeLeft(Math.floor(voiceRef.current.duration));
    }

    if (ambientRef.current) {
      fadeAudio(ambientRef.current, 0, 500, () => {
        ambientRef.current.pause();
        ambientRef.current.currentTime = 0;
      });
    }
    if (voiceRef.current) {
      voiceRef.current.pause();
      voiceRef.current.currentTime = 0;
    }
  };

  const handleThemeSelect = (theme) => {
    if (theme.isPremium && !isUserPremium) {
      setShowPremium(true);
      return;
    }
    setActiveTheme(theme);
    localStorage.setItem("meditation_theme_id", theme.id);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // --- ЗАХИСТ ВІД РЕНДЕРУ ---
  if (!data) return null;

  const currentStep = data.type === "breathing" ? data.cycle[stepIndex] : null;

  return (
    <div className="flex flex-col h-dvh overflow-hidden relative select-none font-sans text-white bg-[#061014]">
      <style>{`
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          height: 14px;
          width: 14px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 0 10px rgba(255,255,255,0.5);
          cursor: pointer;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow { 
          animation: spin-slow 10s linear infinite; 
        }
        @keyframes pop-in {
          0% { opacity: 0; transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-pop-in { animation: pop-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>

      {/* ФОН */}
      {activeTheme.image && (
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-1000 z-0"
          style={{ backgroundImage: `url(${activeTheme.image})` }}
        />
      )}
      <div
        className={`absolute inset-0 bg-gradient-to-b ${activeTheme.overlay} bg-black/10 transition-all duration-1000 z-0`}
      />

      {/* ШАПКА */}
      {!isFinished && (
        <div className="relative z-50 flex justify-center items-center w-full px-6 pt-11">
          <div className="text-xl font-light font-mono tracking-widest text-white/80 bg-black/40 px-4 py-1.5 rounded-full backdrop-blur-sm border border-white/5">
            {formatTime(sessionTimeLeft)}
          </div>
        </div>
      )}

      {/* ЦЕНТРАЛЬНИЙ ЕЛЕМЕНТ */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center -mt-10">
        {isFinished ? (
          <div className="text-center animate-pop-in px-6">
            <div className="w-24 h-24 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(16,185,129,0.3)]">
              <FaHandsPraying className="text-4xl text-emerald-400" />
            </div>
            <h2 className="text-3xl font-bold mb-3">
              {t("meditation.completed", { defaultValue: "Сесію завершено" })}
            </h2>
            <p className="text-white/60 mb-8">{data.title}</p>
            <button
              onClick={() => navigate("/")}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 px-8 py-3 rounded-full font-bold transition-colors"
            >
              {t("meditation.continue", { defaultValue: "Повернутися" })}
            </button>
          </div>
        ) : (
          <>
            <div className="relative flex items-center justify-center w-72 h-72">
              {isGuided ? (
                <>
                  <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
                    style={{
                      width: "220px",
                      height: "220px",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      transform: `translate(-50%, -50%) scale(${1 + vocalIntensity * 0.15})`,
                      opacity: isPlaying ? 0.2 + vocalIntensity * 0.3 : 0,
                      filter: `blur(${10 + vocalIntensity * 10}px)`,
                    }}
                  />
                  <div
                    className="relative w-60 h-60 rounded-full bg-[#111] shadow-2xl flex items-center justify-center border-[4px] border-[#222] z-10 animate-spin-slow"
                    style={{
                      animationPlayState: isPlaying ? "running" : "paused",
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background:
                          "repeating-radial-gradient(circle, #222, #111 2px, #111 4px)",
                      }}
                    />
                    <div className="relative z-10 w-24 h-24 rounded-full border-[3px] border-[#111] overflow-hidden">
                      <img
                        src={data.image}
                        alt={data.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-[#061014] rounded-full shadow-inner z-20 border border-[#222]" />
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="absolute inset-0 rounded-full opacity-30 blur-3xl transition-transform"
                    style={{
                      background: `radial-gradient(circle, rgba(34,211,238,0.4) 0%, transparent 70%)`,
                      transform: `scale(${isPlaying ? currentStep.scale + 0.2 : 1})`,
                      transition: `transform ${isPlaying ? currentStep.duration : 1}s linear`,
                    }}
                  />
                  <div
                    className="relative rounded-full flex items-center justify-center bg-gradient-to-tr from-cyan-500/80 to-teal-500/80"
                    style={{
                      width: "140px",
                      height: "140px",
                      transform: `scale(${isPlaying ? currentStep.scale : 1})`,
                      transition: `transform ${isPlaying ? currentStep.duration : 1}s linear`,
                      boxShadow: `inset 0 0 25px rgba(255,255,255,0.2), 0 0 30px rgba(34,211,238,0.3)`,
                    }}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <p className="text-xl font-bold uppercase tracking-widest text-white/90">
                        {isPlaying
                          ? t(`breathing.phase.${currentStep.phase}`)
                          : t("breathing.ready", { defaultValue: "Готові?" })}
                      </p>
                      {isPlaying && (
                        <p className="text-3xl font-light text-white/80">
                          {stepTimeLeft}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {isGuided && (
              <div className="mt-12 text-center relative z-20">
                <h2 className="text-xl font-bold tracking-wide w-2/3 mx-auto">
                  {data.title}
                </h2>
              </div>
            )}
          </>
        )}
      </div>

      {/* КЕРУВАННЯ */}
      {!isFinished && (
        <div className="relative z-20 pb-12 px-8 mt-auto flex items-center justify-between max-w-sm mx-auto w-full">
          <button
            onClick={() => setIsThemeMenuOpen(true)}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-md border border-white/5"
          >
            <FaMusic className="text-lg text-white/50" />
          </button>

          <button
            onClick={togglePlay}
            disabled={isBuffering}
            className="w-20 h-20 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-lg border border-white/10 text-white shadow-xl hover:scale-105 transition-transform disabled:opacity-70 disabled:hover:scale-100"
          >
            {isBuffering ? (
              <div className="w-8 h-8 border-4 border-white/20 border-t-white rounded-full animate-spin" />
            ) : isPlaying ? (
              <FaPause className="text-3xl text-white/90" />
            ) : (
              <FaPlay className="text-3xl pl-1 text-white/90" />
            )}
          </button>

          <button
            onClick={stopSession}
            disabled={
              !isPlaying &&
              sessionTimeLeft ===
                (data.duration || data.totalDurationMinutes) * 60
            }
            className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-md border border-white/5 disabled:opacity-30"
          >
            <FaStop className="text-lg text-white/50" />
          </button>
        </div>
      )}

      {/* ШТОРКА ВИБОРУ ТЕМИ З ПОВЗУНКОМ ГУЧНОСТІ */}
      {isThemeMenuOpen && (
        <div
          className="absolute inset-0 z-[100] flex flex-col justify-end"
          onClick={() => setIsThemeMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          <div
            className="relative bg-[#070f12]/95 backdrop-blur-xl border-t border-white/10 rounded-t-[2rem] p-6 pb-10 max-h-[75vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-center font-bold text-lg mb-4 text-white/80">
              {t("themes.title", { defaultValue: "Фоновий звук" })}
            </h3>

            {activeTheme.id !== "none" && (
              <div className="mb-6 flex items-center gap-4 bg-white/5 px-5 py-3 rounded-2xl border border-white/5">
                <FaVolumeLow className="text-white/40 text-lg flex-shrink-0" />
                <input
                  type="range"
                  min="0"
                  max="0.5"
                  step="0.01"
                  value={ambientVolume}
                  onChange={handleVolumeChange}
                  className="w-full h-1.5 bg-white/10 rounded-lg appearance-none outline-none"
                />
              </div>
            )}

            <div className="grid grid-cols-4 gap-x-4 gap-y-6">
              {THEMES.map((theme) => {
                const Icon = theme.icon;
                const isActive = activeTheme.id === theme.id;

                return (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeSelect(theme)}
                    className="flex flex-col items-center gap-2 text-center"
                  >
                    <div
                      className={`relative w-14 h-14 flex items-center justify-center rounded-2xl transition-all duration-300 ${isActive ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.2)]" : "bg-white/5 text-white/40 border border-white/5 hover:bg-white/10"}`}
                    >
                      <Icon className="text-2xl" />
                      {theme.isPremium && (
                        <div className="absolute -top-1.5 -right-1.5 bg-slate-900 rounded-full p-1 border border-amber-500/40 shadow-md">
                          <FaCrown className="text-[9px] text-amber-500" />
                        </div>
                      )}
                    </div>
                    <span
                      className={`text-[10px] uppercase font-bold tracking-wider leading-tight ${isActive ? "text-emerald-400" : "text-white/40"}`}
                    >
                      {t(theme.labelKey, { defaultValue: theme.defaultLabel })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* МОДАЛЬНЕ ВІКНО ПРЕМІУМ */}
      {showPremium && (
        <div className="absolute inset-0 z-[200]">
          <Premium showPremium={showPremium} setShowPremium={setShowPremium} />
        </div>
      )}

      {/* АУДІО ТЕГИ */}
      <audio
        ref={ambientRef}
        loop
        preload="auto"
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        onCanPlay={handlePlaying}
      />
      <audio
        ref={voiceRef}
        src={data?.voiceUrl} // Безпечне посилання
        preload="auto"
        onLoadedMetadata={handleAudioLoad}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        onCanPlay={handlePlaying}
      />
    </div>
  );
}

export default MeditationPlayer;
